// src/pages/Admin/AdminOrders.tsx - COMPLETE UPDATED VERSION with ID formatting
import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../api';
import { motion } from 'framer-motion';
import { Search, Filter, Eye, Calendar, MapPin } from 'lucide-react';
import { formatCurrency, formatDate, getStatusColor } from '../../utils';
import { formatDisplayId } from '../../utils/idGenerator';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';

interface Order {
  _id: string;
  orderId: string;
  customer: { fullName: string; email: string; _id: string };
  rider: { fullName: string; email: string; _id: string };
  serviceType: 'ride' | 'delivery';
  pickupLocation: string;
  dropoffLocation: string;
  fixedPrice: number;
  platformFee: number;
  riderPayout: number;
  status: string;
  commitmentFeePaid: boolean;
  remainingAmountPaid: boolean;
  createdAt: string;
  completedAt?: string;
}

const AdminOrders: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 20;

  useEffect(() => {
    fetchOrders();
  }, [page, statusFilter, search]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getOrderManagement({
        page,
        limit,
        status: statusFilter || undefined,
      });
      if (res.data.success) {
        setOrders(res.data.data.orders);
        setTotalPages(res.data.data.pagination.pages);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (orderId: string, newStatus: string) => {
    const reason = newStatus === 'cancelled' ? prompt('Reason for cancellation:') : undefined;
    try {
      const res = await adminAPI.updateOrderStatus(orderId, { status: newStatus, reason });
      if (res.data.success) {
        toast.success('Order status updated');
        fetchOrders();
      }
    } catch (error) {
      toast.error('Failed to update order');
    }
  };

  const filteredOrders = orders.filter(order => 
    formatDisplayId(order.orderId).toLowerCase().includes(search.toLowerCase()) ||
    order.customer?.fullName?.toLowerCase().includes(search.toLowerCase()) ||
    order.rider?.fullName?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <h1 className="text-3xl font-bold mb-6">Order Management</h1>

      <div className="bg-white rounded-2xl p-4 shadow-lg mb-6">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by Order ID, Customer, or Rider..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg"
              />
            </div>
          </div>
          <div className="w-40">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="accepted">Accepted</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="expired">Expired</option>
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">Loading...</div>
      ) : (
        <>
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Order ID</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Customer</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Rider</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Route</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Amount</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Status</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Payment</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Date</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredOrders.map((order) => (
                    <tr key={order._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-mono text-sm">
                        <span title={order.orderId}>{formatDisplayId(order.orderId)}</span>
                      </td>
                      <td className="px-6 py-4">{order.customer?.fullName || 'N/A'}</td>
                      <td className="px-6 py-4">{order.rider?.fullName || 'N/A'}</td>
                      <td className="px-6 py-4 max-w-xs">
                        <div className="flex items-center gap-1 text-xs">
                          <MapPin className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">{order.pickupLocation} → {order.dropoffLocation}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 font-bold">{formatCurrency(order.fixedPrice)}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                          {order.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {order.commitmentFeePaid ? (
                          <span className="text-green-600 text-xs">10% paid</span>
                        ) : (
                          <span className="text-gray-400 text-xs">Not paid</span>
                        )}
                        {order.remainingAmountPaid && (
                          <span className="ml-1 text-green-600 text-xs">90% paid</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm">{formatDate(order.createdAt)}</td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <select
                            value={order.status}
                            onChange={(e) => handleUpdateStatus(order._id, e.target.value)}
                            className="px-2 py-1 text-xs border rounded"
                          >
                            <option value="pending">Pending</option>
                            <option value="accepted">Accepted</option>
                            <option value="in_progress">In Progress</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancel</option>
                          </select>
                          <Link
                            to={`/order/${order._id}`}
                            target="_blank"
                            className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg"
                            title="View Order"
                          >
                            <Eye className="h-4 w-4" />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex justify-center mt-6 gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 border rounded-lg disabled:opacity-50"
            >
              Previous
            </button>
            <span className="px-4 py-2">Page {page} of {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 border rounded-lg disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminOrders;